import {
  TableRow, TableCell, Grid, Typography, TextField, Button, Dialog, DialogActions, DialogContent,
  DialogTitle, Alert, Snackbar, Box, Tooltip, useMediaQuery, IconButton, useTheme, Chip,
} from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { AddOrderItemRequest, Item, client } from '../../../../app/services/api/orderManagementClient';
import { PromotionForCustomerDto } from '../../../../app/services/api/promotions';
import { ChangeEvent, forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { applyRoundAndFormat, formatCaseFillPercent, formatPalletQty, formatPromotionReservationDuration } from '../../../../utils';
import useLogError from '../../../../hooks/useLogError';
import { UserContext } from '../../../../components/shared/useUser';
import { System } from '../../../../enums/System';
import { isOrderCategory, OrderCategoriesTranslationMap } from '../../../../enums/OrderCategories';
import TextWithOverflowTooltip from '../../../../components/shared/TextWithOverflowTooltip';
import { useNavigate } from 'react-router-dom';
import { PredictedDemandModel } from '../../../../app/services/api/podClient';
import { MagicWand01 } from '../../../../assets';
import { QuantityWarningDialog } from '../../../../components/shared/orders/QuantityWarningDialog';
import NoProductImage from '../../../../assets/images/NoProductImage.png';
import { debounce } from 'lodash';
import MessagesSnackbar from '../../../../components/shared/MessagesSnackbar';

type ProductRowProps = {
  product: Item;
  selectedOrderId?: number;
  basketQuantity?: number;
  promoBasketQuantity?: number;
  promotion?: PromotionForCustomerDto;
  onAddItemConfirmed: () => void;
  onAddPromoToOrder?: (product: Item, promotion: PromotionForCustomerDto, quantity: number) => Promise<{ success: boolean; message?: string }>;
  currencyCode?: string;
  showMessageWhenNoCost: boolean;
  predictedItem?: PredictedDemandModel;
  showPredictedQuantity?: boolean;
  canEditPredictions?: boolean;
  orderType: number;
  orderItemId?: number;
}

const ProductRow = forwardRef<{ matchPredictedQuantities: () => void } | null, ProductRowProps>(({ product, selectedOrderId, basketQuantity, promoBasketQuantity = 0, promotion, currencyCode, onAddItemConfirmed, onAddPromoToOrder, showMessageWhenNoCost, orderType, predictedItem, showPredictedQuantity, canEditPredictions = true, orderItemId }, ref) => {
  const navigate = useNavigate();
  const { t } = useTranslation('products');
  const theme = useTheme();
  const { logError } = useLogError();
  const { selectedStore, hasPermissionTo, selectedSystem } = useContext(UserContext);
  const showStockLevels = selectedSystem?.id !== System.OrderlyForDistributors;
  const [quantity, setQuantity] = useState<string>(basketQuantity?.toString() ?? '0');
  const [startingQuantity, setStartingQuantity] = useState<string>(basketQuantity?.toString() ?? '0');
  const [promoQuantity, setPromoQuantity] = useState<string>(promoBasketQuantity?.toString() ?? '0');
  const [promoStartingQuantity, setPromoStartingQuantity] = useState<string>(promoBasketQuantity?.toString() ?? '0');
  const [, setLoading] = useState<boolean>(false);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const predictedDemandItem = predictedItem?.predictedDemandItems?.at(0);
  const predictedQuantity = predictedDemandItem?.quantity.toString();
  const showPrediction = showPredictedQuantity && predictedDemandItem?.hasPrediction;
  const borderColor = showPrediction && predictedQuantity === quantity ? theme.palette.success[400] : '';
  const safetyStock = predictedDemandItem?.safetyStock;
  const predictedDemandQuantity = predictedDemandItem?.predictedDemandQuantity;
  const [showQuantityWarningDialog, setShowQuantityWarningDialog] = useState<boolean>(false);
  const [warningMessage, setWarningMessage] = useState<string>('');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'warning' | 'info' | 'success';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));

  useEffect(() => {
    setQuantity(basketQuantity?.toString() ?? '0');
    setStartingQuantity(basketQuantity?.toString() ?? '0');
  }, [basketQuantity]);

  useEffect(() => {
    setPromoQuantity(promoBasketQuantity?.toString() ?? '0');
    setPromoStartingQuantity(promoBasketQuantity?.toString() ?? '0');
  }, [promoBasketQuantity]);

  const onQuantityChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    event.preventDefault();
    const value = Math.abs(Number(event.target.value || '0'));
    setQuantity(value.toString());
  };

  const handleMatchQuantityChange = () => {
    setQuantity(predictedQuantity?.toString() ?? '');
  };

  useImperativeHandle(ref, () => ({
    matchPredictedQuantities,
  }));

  const matchPredictedQuantities = () => {
    if (showPrediction) {
      setQuantity(predictedQuantity ?? quantity);
    }
  };

  const debouncedAddItemToOrder = useCallback(
    debounce((itemId: number, quantity: number) => {
      onAddItemToOrder(itemId, quantity);
    }, 500),
    [],
  );

  const onAddItemToOrder = async (itemId: number, quantity: number) => {
    try {
      setLoading(true);
      const request: AddOrderItemRequest = {
        orderId: selectedOrderId !== undefined ? selectedOrderId : 0,
        itemId,
        quantity,
        predictedQuantity: quantity,
      };
      const response = await client.addOrderItem(selectedStore?.storeNumber, request);
      if (response.success) {
        handleSnackbarOpen(t('addItemSuccessMessage'), 'success');
        setQuantity(quantity.toString());
        setStartingQuantity(quantity.toString());
        onAddItemConfirmed();
      }
      else {
        setWarningMessage(response.message);
        setShowQuantityWarningDialog(true);
        //fixQuantity();
      }
    } catch (error) {
      handleSnackbarOpen(t('addItemErrorMessage') + ' ' + error, 'error');
      logError(error);
    } finally {
      setLoading(false);
      setShowDialog(false);
    }
  };

  const handleDeleteOrderItem = async (orderId: number, orderItemId: number) => {
    if (!orderId || !orderItemId) return;
    setLoading(true);
    try {
      const result = await client.deleteOrderItem(selectedStore?.storeNumber, orderId.toString(), orderItemId.toString());
      if (result.success) {
        handleSnackbarOpen(t('deleteSuccess'), 'success');
        onAddItemConfirmed();
      } else {
        handleSnackbarOpen(t('deleteItemFail'), 'error');
      }
    } catch (error) {
      handleSnackbarOpen(t('deleteItemFail') + ' ' + error, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarOpen = (message: string, severity: 'error' | 'warning' | 'info' | 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const hideDialogAndResetQuantity = () => {
    setShowDialog(false);
    setQuantity(startingQuantity);
  };

  const quantityError = (): string | null => {
    if (Number(quantity) == 0) {
      return null;
    }

    if ((product.maxQuantity != null) && Number(quantity) > product.maxQuantity)
      return `${t('maxQuantityAllowedIs')}: ${product.maxQuantity}`;

    if ((product.minQuantity != null) && Number(quantity) < product.minQuantity)
      return `${t('minQuantityAllowedIs')}: ${product.minQuantity}`;

    return null;
  };

  const formatPromoDate = (dateUtc: string) => new Date(dateUtc).toLocaleDateString();

  const getPromoEndDate = (endDateUtc?: string) => {
    if (!endDateUtc) {
      return null;
    }
    const end = new Date(endDateUtc);
    if (Number.isNaN(end.getTime()) || end.getFullYear() <= 1) {
      return null;
    }
    return formatPromoDate(endDateUtc);
  };

  const hasPromotion = Boolean(promotion);
  const hasPromoQuantity = Boolean(promotion && (promotion.remainingQuantity ?? 0) > 0);
  const promoMax = promotion ? promoBasketQuantity + (promotion.remainingQuantity ?? 0) : null;
  const promoUnitPrice = promotion
    ? promotion.pricingType === 2
      ? promotion.fixedPromoUnitPrice
      : product.price * (1 - (promotion.discountPercentage ?? 0) / 100)
    : null;

  const renderPromoTooltipContent = () => {
    if (!promotion || promoUnitPrice == null) {
      return null;
    }

    const reservationDuration = formatPromotionReservationDuration(promotion.reservationDurationMinutes);

    return (
      <Box sx={{ p: 0.5 }}>
        <Typography sx={{ fontSize: '12px', fontWeight: 600, mb: 0.5 }}>{promotion.name}</Typography>
        {promotion.promoDescription && (
          <Typography sx={{ fontSize: '11px', mb: 0.5 }}>{promotion.promoDescription}</Typography>
        )}
        <Typography sx={{ fontSize: '11px' }}>
          {promotion.discountPercentage != null
            ? t('promoDiscountAppliedPerUnit', { discount: promotion.discountPercentage })
            : t('promoFixedPricePerUnit', { price: applyRoundAndFormat(promoUnitPrice, currencyCode) })}
        </Typography>
        <Typography sx={{ fontSize: '11px', mt: 0.25 }}>
          {t('promoAvailable', { remaining: promotion.remainingQuantity, cap: promotion.quantityCap })}
        </Typography>
        {reservationDuration && (
          <Typography sx={{ fontSize: '11px', mt: 0.25 }}>
            {t('promoReservationDuration', { duration: reservationDuration })}
          </Typography>
        )}
      </Box>
    );
  };

  const renderPromoSummary = () => {
    if (!hasPromotion || !promotion || promoUnitPrice == null) {
      return null;
    }

    const promoEndDate = getPromoEndDate(promotion.endDateUtc);

    return (
      <Box sx={{ mt: 1 }}>
        <Chip
          icon={<LocalOfferIcon sx={{ fontSize: 14 }} />}
          size="small"
          label={t('promoAllocation', {
            quantity: promotion.remainingQuantity,
            unitPrice: applyRoundAndFormat(promoUnitPrice, currencyCode),
          })}
          sx={{
            bgcolor: '#E8F5E9',
            color: '#2E7D32',
            fontWeight: 600,
            fontSize: '12px',
            height: 24,
            '& .MuiChip-icon': { color: '#2E7D32' },
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          <Typography sx={{ fontSize: '13px', color: theme.palette.custom.gray[400], textDecoration: 'line-through' }}>
            {applyRoundAndFormat(product.price, currencyCode)}
          </Typography>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#2E7D32' }}>
            {applyRoundAndFormat(promoUnitPrice, currencyCode)} each
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
          <Typography sx={{ fontSize: '12px', color: '#2E7D32', fontWeight: 500 }}>
            {promotion.remainingQuantity} promo units available
          </Typography>
          <Tooltip title={renderPromoTooltipContent()}
            arrow>
            <InfoOutlinedIcon sx={{ fontSize: 14, color: theme.palette.custom.gray[400], cursor: 'pointer' }} />
          </Tooltip>
        </Box>
        {promoEndDate && (
          <Typography sx={{ fontSize: '12px', color: theme.palette.custom.gray[500], mt: 0.25 }}>
            {t('promoEndDate', { date: promoEndDate })}
          </Typography>
        )}
      </Box>
    );
  };

  const renderProductDetailsColumn = () => (
    <>
      <TextWithOverflowTooltip text={product.name} />
      <TextWithOverflowTooltip text={`${t('itemNumber')}: ${product.sku}`} />
      <TextWithOverflowTooltip text={isOrderCategory(product.category)
        ? t(OrderCategoriesTranslationMap[product.category as keyof typeof OrderCategoriesTranslationMap])
        : product.category} />
      {(showMessageWhenNoCost || product.price === 0) ?
        <TextWithOverflowTooltip text='' />
        :
        <TextWithOverflowTooltip text={applyRoundAndFormat(product.price, currencyCode)} />
      }
    </>
  );

  const renderProductStockColumn = () => (
    <>
      <TextWithOverflowTooltip text={`${t('orderableUom')}: ${product.orderableUom} (${product.packSize}${product.primaryUom})`} />
      <TextWithOverflowTooltip text={`${t('primaryUom')}: ${product.primaryUom}`} />
      <Tooltip title={t('palletQtyTooltip')}
        arrow>
        <Box style={{ whiteSpace: 'nowrap', overflow: 'visible', textOverflow: 'clip' }}>
          {t('palletQty')}: {formatPalletQty(product.casesPerPallet)}
        </Box>
      </Tooltip>
      <Tooltip title={t('casePercentTooltip')}
        arrow>
        <Box style={{ whiteSpace: 'nowrap', overflow: 'visible', textOverflow: 'clip' }}>
          {t('casePercent')}: {formatCaseFillPercent(product.casesPerPallet)}
        </Box>
      </Tooltip>
      {showStockLevels && (
        <>
          <Tooltip title={`${t('stockOnHand')}: ${product.stockOnHand}`}
            arrow>
            <Box style={{ whiteSpace: 'nowrap', overflow: 'visible', textOverflow: 'clip' }}>
              {t('soh')} {product.stockOnHand}
            </Box>
          </Tooltip>
          <Tooltip title={`${t('stockOnOrder')}: ${product.stockOnOrder}`}
            arrow>
            <Box style={{ whiteSpace: 'nowrap', overflow: 'visible', textOverflow: 'clip' }}>
              {t('soo')}: {product.stockOnOrder}
            </Box>
          </Tooltip>
        </>
      )}
    </>
  );

  return (
    <>
      <TableRow key={product.id}>
        <TableCell sx={{ width: '80%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <Grid container
            alignItems={'center'}
            spacing={1}
            onClick={hasPermissionTo(['ProductDetailView']) ? () => navigate(`/ofd/products/${product.sku}`, {
              state: {
                orderId: selectedOrderId,
                fromOrderPage: selectedOrderId ? true : false,
                canChangeOrderQuantity: true,
                quantity: basketQuantity || 0,
                orderItemId: orderItemId,
                fromOrderableItems: true,
              },
            }) : undefined}
            wrap="nowrap">
            <Grid item
              xs="auto">
              <Box
                style={{
                  width: '100px',
                  height: isLargeScreen ? '75px' : '100px',
                  justifyContent: 'center',
                  backgroundColor: (product?.thumbnailUrl ?? '') != '' ? theme.palette.common.white : theme.palette.custom.gray[200],
                  borderRadius: '8px',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #ddd',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'contain',
                  imageRendering: '-webkit-optimize-contrast',
                  backgroundImage: `url(${product.thumbnailUrl && product.thumbnailUrl.trim() !== '' ? product.thumbnailUrl : NoProductImage})`,

                }}
              />
            </Grid>
            <Grid item
              sx={{ minWidth: 0, paddingLeft: '10px !important', flex: 1 }}>
              {isLargeScreen ? (
                <Grid container
                  spacing={2}>
                  <Grid item
                    xs={6}>
                    {renderProductDetailsColumn()}
                  </Grid>
                  <Grid item
                    xs={6}>
                    {renderProductStockColumn()}
                  </Grid>
                  {/* Promotion info below product details */}
                  {hasPromotion && (
                    <Grid item
                      xs={12}>
                      {renderPromoSummary()}
                    </Grid>
                  )}
                </Grid>
              ) : (
                <>
                  {product.name}  <br />
                  {t('itemNumber')}: {product.sku}   <br />
                  {isOrderCategory(product.category)
                    ? t(OrderCategoriesTranslationMap[product.category as keyof typeof OrderCategoriesTranslationMap])
                    : product.category}   <br />
                  {(showMessageWhenNoCost || product.price === 0) ?
                    ''
                    :
                    applyRoundAndFormat(product.price, currencyCode)
                  } <br />
                  {t('orderableUom')}: {product.orderableUom} ({product.packSize}{product.primaryUom})<br />
                  {t('primaryUom')}: {product.primaryUom}  <br />
                  {t('palletQty')}: {formatPalletQty(product.casesPerPallet)}  <br />
                  {t('casePercent')}: {formatCaseFillPercent(product.casesPerPallet)}  <br />
                  {showStockLevels && (
                    <>
                      <Tooltip title={`${t('stockOnHand')}: ${product.stockOnHand}`}
                        arrow>
                        <Box style={{ whiteSpace: 'nowrap', overflow: 'visible', textOverflow: 'clip' }}>
                          {t('soh')} {product.stockOnHand}
                        </Box>
                      </Tooltip>
                      <Tooltip title={`${t('stockOnOrder')}: ${product.stockOnOrder}`}
                        arrow>
                        <Box style={{ whiteSpace: 'nowrap', overflow: 'visible', textOverflow: 'clip' }}>
                          {t('soo')}: {product.stockOnOrder}
                        </Box>
                      </Tooltip>
                    </>
                  )}
                  {/* Promotion info for small screens */}
                  {renderPromoSummary()}
                </>
              )}
            </Grid>
          </Grid>
        </TableCell>
        <TableCell sx={{ verticalAlign: 'top', minWidth: 160 }}>
          {/* Normal qty section */}
          <Box>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: theme.palette.custom.gray[500], mb: 0.5 }}>
              {promotion ? t('normalQty') : t('quantity')}
            </Typography>
            <TextField
              label={showPrediction ? t('predictedQuantity', { value: predictedQuantity }) : null}
              error={(quantityError()?.length ?? 0) > 0}
              helperText={quantityError()}
              size='small'
              type='number'
              inputProps={{ min: 0 }}
              autoComplete='off'
              disabled={showPrediction && !canEditPredictions}
              sx={{
                maxWidth: 100,
                width: '100%',
                input: {
                  textAlign: 'center',
                },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: borderColor,
                  },
                },
                '& .MuiInputLabel-root': {
                  color: borderColor,
                },
              }}
              value={quantity}
              onChange={e => {
                const value = e.target.value;
                if (/^\d*$/.test(value)) {
                  setQuantity(value);
                }
              }}
              onFocus={e => e.target.select()}
              onBlur={async e => {
                let newQuantity = Number(e.target.value || '0');
                if (isNaN(newQuantity) || newQuantity < 0) newQuantity = 0;
                if (newQuantity.toString() !== startingQuantity) {
                  if (newQuantity === 0 && selectedOrderId && orderItemId) {
                    await handleDeleteOrderItem(selectedOrderId, orderItemId);
                  } else {
                    debouncedAddItemToOrder(product.id, newQuantity);
                    setStartingQuantity(newQuantity.toString());
                  }
                }
              }}
            />
            {hasPromotion && !showMessageWhenNoCost && product.price > 0 && (
              <Typography sx={{ fontSize: '11px', color: theme.palette.custom.gray[400], mt: 0.25 }}>
                at {applyRoundAndFormat(product.price, currencyCode)} each
              </Typography>
            )}
          </Box>
          {
            showPrediction &&
            <Tooltip title={
              <span>
                {
                  predictedDemandQuantity != null && (
                    <>
                      {t('predictedDemandQuantity', { value: predictedDemandQuantity, uom: product.primaryUom })}
                      <br />
                    </>
                  )
                }
                {safetyStock != null && t('safetyStock', { value: safetyStock, uom: product.primaryUom })}
              </span>
            }>
              <IconButton aria-label={t('applyPredictedQuantity')}
                disabled={!canEditPredictions}
                onClick={() => handleMatchQuantityChange()}>
                <MagicWand01
                  color={predictedQuantity === quantity ? theme.palette.success[700] : theme.palette.custom.gray[400]} />
              </IconButton>
            </Tooltip>
          }

          {/* Promo qty section */}
          {hasPromotion && promotion && promoUnitPrice != null && (
            <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${theme.palette.custom.gray[200]}` }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: theme.palette.custom.gray[500], mb: 0.5 }}>
                {t('promoQty')}
              </Typography>
              <TextField
                size="small"
                type="number"
                inputProps={{ min: 0 }}
                autoComplete="off"
                disabled={promotion.remainingQuantity <= 0 && parseInt(promoStartingQuantity || '0', 10) <= 0}
                sx={{
                  maxWidth: 100,
                  width: '100%',
                  input: { textAlign: 'center' },
                  '& .MuiOutlinedInput-root fieldset': { borderColor: '#2E7D32' },
                  '& .MuiOutlinedInput-root:hover fieldset': { borderColor: '#1B5E20' },
                }}
                value={promoQuantity}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^\d*$/.test(val)) {
                    const num = parseInt(val || '0', 10);
                    if (promoMax != null && num > promoMax) {
                      setPromoQuantity(promoMax.toString());
                    } else {
                      setPromoQuantity(val);
                    }
                  }
                }}
                onFocus={(e) => e.target.select()}
                onBlur={async (e) => {
                  let newQty = parseInt(e.target.value || '0', 10);
                  if (promoMax != null && newQty > promoMax) newQty = promoMax;
                  setPromoQuantity(newQty.toString());
                  if (!isNaN(newQty) && newQty.toString() !== promoStartingQuantity && onAddPromoToOrder && promotion) {
                    const result = await onAddPromoToOrder(product, promotion, newQty);

                    if (result.success) {
                      handleSnackbarOpen(t('addItemSuccessMessage'), 'success');
                    } else {
                      setPromoQuantity(promoStartingQuantity);
                      handleSnackbarOpen(result.message || t('addItemErrorMessage'), 'error');
                    }
                  }
                }}
              />
              <Typography sx={{ fontSize: '11px', color: promotion.remainingQuantity <= 0 ? theme.palette.error.main : '#2E7D32', mt: 0.25 }}>
                {promotion.remainingQuantity <= 0
                  ? t('promoSoldOut')
                  : <>at {applyRoundAndFormat(promoUnitPrice, currencyCode)} each
                    <Typography component="span"
                      sx={{ fontSize: '11px', color: theme.palette.custom.gray[400], ml: 0.5 }}>
                      (max {promoMax})
                    </Typography>
                  </>
                }
              </Typography>
            </Box>
          )}
        </TableCell>
      </TableRow>
      <Dialog open={showDialog}
        onClose={() => setShowDialog(false)}
        maxWidth='sm'
        fullWidth>
        <DialogTitle>
          {t('confirmDialogTitle')}
        </DialogTitle>
        <DialogContent>
          <Grid container>
            <Grid item
              xs={12}>
              <Typography variant='textMD'>{t('confirmDialogContent', { item: product.name })}</Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions >
          <Button
            variant="secondary"
            onClick={hideDialogAndResetQuantity}>
            {t('cancel')}
          </Button>
          <Button variant="primary"
            onClick={() => onAddItemToOrder(product.id, Number(quantity))}>
            {t('update')}
          </Button>
        </DialogActions>
      </Dialog>
      <QuantityWarningDialog setShowQuantityWarningDialog={setShowQuantityWarningDialog}
        message={warningMessage}
        showDialog={showQuantityWarningDialog} />
      <MessagesSnackbar open={snackbar.open}
        onClose={handleSnackbarClose}
        message={snackbar.message}
        severity={snackbar.severity}
        duration={3000} />
    </>
  );
});

ProductRow.displayName = 'ProductRow';

export default ProductRow;